import { supabase, isConfigured } from './supabase';
import type { Team, TeamMember } from '../types';

export async function getOrCreateDefaultTeam(userId: string): Promise<Team | null> {
  if (!isConfigured) return null;

  // First check if user owns a team
  const { data: ownedTeam } = await supabase
    .from('teams')
    .select('*')
    .eq('owner_id', userId)
    .limit(1)
    .single();

  if (ownedTeam) return ownedTeam as Team;

  // Check if user is a member of any team
  const { data: membership } = await supabase
    .from('team_members')
    .select('team_id')
    .eq('user_id', userId)
    .limit(1)
    .single();

  if (membership) {
    const { data: memberTeam } = await supabase
      .from('teams')
      .select('*')
      .eq('id', membership.team_id)
      .single();
    if (memberTeam) return memberTeam as Team;
  }

  const { data: created, error } = await supabase
    .from('teams')
    .insert({
      name: 'My Team',
      owner_id: userId,
    })
    .select()
    .single();

  if (error) {
    console.error('[teams] create default team failed:', error);
    return null;
  }

  await supabase.from('team_members').insert({
    team_id: created.id,
    user_id: userId,
    role: 'owner',
  });

  await supabase.rpc('seed_sales_pipeline_stages', { p_team_id: created.id });

  return created as Team;
}

export async function getTeamMembers(teamId: string): Promise<TeamMember[]> {
  if (!isConfigured) return [];

  const { data: rows, error } = await supabase
    .from('team_members')
    .select('id, team_id, user_id, role, created_at')
    .eq('team_id', teamId)
    .order('created_at', { ascending: true });

  if (error || !rows || rows.length === 0) return rows ? (rows as TeamMember[]) : [];

  const userIds = [...new Set((rows as { user_id: string }[]).map((r) => r.user_id))];
  const { data: profiles } = await supabase
    .from('profiles')
    .select('id, email')
    .in('id', userIds);

  const emailByUserId = new Map<string, string>();
  (profiles || []).forEach((p: { id: string; email?: string }) => {
    if (p.email) emailByUserId.set(p.id, p.email);
  });

  return (rows as TeamMember[]).map((r) => ({
    ...r,
    email: emailByUserId.get(r.user_id),
  }));
}

export async function getTeam(teamId: string): Promise<Team | null> {
  if (!isConfigured) return null;
  const { data, error } = await supabase
    .from('teams')
    .select('*')
    .eq('id', teamId)
    .single();
  if (error || !data) return null;
  return data as Team;
}
